import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { StringUtils } from '../../../../../core/utils/string_utils';

const initialState = {
    name: "",
    email: "",
    password: "",
    confirmPassword: "",
    nameErrorMessage: "",
    emailErrorMessage: "",
    passwordErrorMessage: "",
    confirmPasswordErrorMessage: "",
    registerErrorMessage: "",
    isAgree: false,
    isCreateOngoing: false,
    goToLogin: false,
    goToOtp: false,
}

export const createCreateAccountSlice = (userRepository) => {
    return createSlice({
        name: "createAccount",
        initialState,
        reducers: {
            resetState: () => initialState,
            setName(state, action) {
                const name = action.payload.trim();
                state.name = name;
                if (!name) {
                    state.nameErrorMessage = "Name is required";
                } else {
                    state.nameErrorMessage = "";
                }
            },
            setEmail(state, action) {
                const email = action.payload.trim();
                state.email = email;

                const emailValidResult = StringUtils.isEmailValid(email);
                state.emailErrorMessage = emailValidResult.message;
            },
            setPassword(state, action) {
                const password = action.payload.trim();
                state.password = password;

                const passwordValidResult = StringUtils.isPasswordValid(password);
                state.passwordErrorMessage = passwordValidResult.message;
            },
            setConfirmPassword(state, action) {
                const confirmPassword = action.payload.trim();
                state.confirmPassword = confirmPassword;

                if (!confirmPassword) {
                    state.confirmPasswordErrorMessage = "Confirm Password is required";
                } else if (confirmPassword !== state.password) {
                    state.confirmPasswordErrorMessage = "Confirm Password is incorrect";
                } else {
                    state.confirmPasswordErrorMessage = "";
                }
            },
            setAgree(state, action) {
                state.isAgree = action.payload;
            }
        },
        extraReducers: (builder) => {
            builder
                .addCase(userCreateAccount.pending, (state) => {
                    state.isCreateOngoing = true;
                    state.registerErrorMessage = "";
                })
                .addCase(userCreateAccount.fulfilled, (state, action) => {
                    state.isCreateOngoing = false;
                    const success = action.payload.success;
                    if (success) {
                        state.goToOtp = true;
                    } else {
                        state.registerErrorMessage = action.payload.message;
                    }
                })
                .addCase(userCreateAccount.rejected, (state, action) => {
                    state.isCreateOngoing = false;
                    const nameErrorMessage = action.payload?.nameErrorMessage ?? "";
                    const emailErrorMessage = action.payload?.emailErrorMessage ?? "";
                    const passwordErrorMessage = action.payload?.passwordErrorMessage ?? "";
                    const confirmPasswordErrorMessage = action.payload?.confirmPasswordErrorMessage ?? "";
                    const agreeErrorMessage = action.payload?.agreeErrorMessage ?? "";
                    state.nameErrorMessage = nameErrorMessage;
                    state.emailErrorMessage = emailErrorMessage;
                    state.passwordErrorMessage = passwordErrorMessage;
                    state.confirmPasswordErrorMessage = confirmPasswordErrorMessage;
                    state.registerErrorMessage = agreeErrorMessage;
                })
        }
    });
}

export const userCreateAccount = createAsyncThunk(
    "createAccount/userCreateAccount",
    async (credentials, { getState, rejectWithValue, extra }) => {
        const userRepository = extra.userRepository;
        const state = getState().createAccount;
        const nameVal = state.name.trim();
        const emailVal = state.email.trim();
        const passwordVal = state.password.trim();
        const confirmPasswordVal = state.confirmPassword.trim();
        const isAgree = state.isAgree;

        let success = true;
        let nameErrorMessage = "";
        let emailErrorMessage = "";
        let passwordErrorMessage = "";
        let confirmPasswordErrorMessage = "";
        let agreeErrorMessage = "";

        if (!nameVal) {
            nameErrorMessage = "Name is required";
            success = false;
        }

        const emailValidResult = StringUtils.isEmailValid(emailVal);
        if (!emailValidResult.isValid) {
            emailErrorMessage = emailValidResult.message;
            success = false;
        }

        const passwordValidResult = StringUtils.isPasswordValid(passwordVal);
        if (!passwordValidResult.isValid) {
            passwordErrorMessage = passwordValidResult.message;
            success = false;
        }

        if (!confirmPasswordVal) {
            confirmPasswordErrorMessage = "Confirm Password is required";
            success = false;
        }

        if (!isAgree) {
            agreeErrorMessage = "Please agree to terms and conditions";
            success = false;
        }

        if (!success) {
            return rejectWithValue({
                nameErrorMessage,
                emailErrorMessage,
                passwordErrorMessage,
                confirmPasswordErrorMessage,
                agreeErrorMessage,
            });
        } else {
            const result = await userRepository.register(nameVal, emailVal, passwordVal);
            return result;
        }
    },
);